import type { Block } from '../../domain/videoAnnotation';

/**
 * Die Rechnung hinter den beiden Leisten unter dem Video: Pixel ↔ Millisekunde, und was unter
 * dem Finger liegt.
 *
 * Eigene, React-freie Datei — dieselbe Begründung wie bei `reducer.ts`: das Projekt hat keine
 * Infrastruktur zum Rendern von Bauteilen (`vitest.config.ts`, `environment: 'node'`), und
 * genau hier sitzen die Fehler, die man am Bildschirm erst als „lässt sich nicht greifen"
 * bemerkt.
 */

/**
 * Wie breit die Trefferzone eines Blockrandes ist.
 *
 * ⚠️ **Breiter als der gezeichnete Rand**, und sie reicht auch etwas **ausserhalb** des Blocks.
 * Ein zwei Pixel breiter Strich ist mit dem Finger nicht zu fassen; die Apple-Faustregel von
 * 44 pt für eine Schaltfläche wäre hier zu viel — ein kurzer Block bestünde dann nur noch aus
 * Rändern. 22 ist der Mittelweg: mit der Fingerkuppe erreichbar, ohne die Mitte aufzuzehren.
 */
export const ANFASSER_PX = 22;

const grenze = (wert: number, min: number, max: number) => Math.min(Math.max(wert, min), max);

/**
 * Welcher Zeitpunkt liegt an dieser Stelle der Leiste?
 *
 * ⚠️ **Gedeckelt.** Ein Finger rutscht über den Rand der Leiste hinaus, sobald man zügig zieht;
 * ohne Deckel spränge die Wiedergabe hinter das Videoende, und `currentTime` ist dort
 * undefiniert.
 */
export const msAusX = (x: number, breite: number, durationMs: number): number =>
  grenze(Math.round((x / breite) * durationMs), 0, durationMs);

export const xAusMs = (ms: number, breite: number, durationMs: number): number =>
  (ms / durationMs) * breite;

/** Wie viele Spuren die Blockleiste hoch ist — mindestens eine, auch wenn noch nichts da ist. */
export const spurAnzahl = (blocks: Block[]): number =>
  blocks.reduce((tiefste, b) => Math.max(tiefste, b.track + 1), 1);

/** Welcher Teil eines Blocks unter dem Finger liegt. */
export type Teil = 'anfang' | 'mitte' | 'ende';

export interface Lage {
  /** Breite der Leiste in Pixeln. */
  breite: number;
  durationMs: number;
  /** Höhe einer Spurzeile in Pixeln. */
  spurHoehe: number;
}

/**
 * Was liegt an dieser Stelle der Blockleiste?
 *
 * ⚠️ **Die Spur entscheidet, welcher Block gemeint ist** — nicht die Stapelfolge. Überschneidende
 * Blöcke liegen in verschiedenen Zeilen, und der Finger zeigt auf eine davon.
 *
 * ⚠️ **Bei einem sehr schmalen Block gewinnt das Verschieben.** Lägen die beiden Anfasser
 * übereinander, gäbe es keine Mitte mehr, und der Block liesse sich nie wieder bewegen — man
 * könnte ihn nur noch weiter zerkleinern. Wer ihn verlängern will, zieht ihn erst an eine
 * bequeme Stelle.
 */
export function treffer(
  blocks: Block[],
  punkt: { x: number; y: number },
  lage: Lage,
): { id: string; teil: Teil } | null {
  const spur = Math.floor(punkt.y / lage.spurHoehe);

  for (const b of blocks) {
    if (b.track !== spur) continue;

    const links = xAusMs(b.fromMs, lage.breite, lage.durationMs);
    const rechts = xAusMs(b.toMs, lage.breite, lage.durationMs);
    if (punkt.x < links - ANFASSER_PX / 2 || punkt.x > rechts + ANFASSER_PX / 2) continue;

    // Die Anfasser dürfen zusammen höchstens zwei Drittel des Blocks einnehmen, damit ein
    // greifbarer Rest in der Mitte bleibt.
    const anfasser = Math.min(ANFASSER_PX, (rechts - links) / 3);

    if (punkt.x <= links + anfasser / 2) return { id: b.id, teil: 'anfang' };
    if (punkt.x >= rechts - anfasser / 2) return { id: b.id, teil: 'ende' };
    return { id: b.id, teil: 'mitte' };
  }

  return null;
}
